import { useEffect, useRef } from 'react';

/** How often the totals are read again while documents are processing. */
export const PENDING_RECHECK_MS = 10_000;
export const PENDING_RECHECK_MAX_MS = 60_000;

/**
 * Documents still processing are not pushed: read the Q&A's totals again
 * until they are ready (attachments are also followed by the files-status
 * poll), less and less often, and not while the tab is hidden. Back on the
 * tab they are read at once, then from the shortest interval again; a read
 * on return happens at most once per shortest interval, however often the
 * user switches tabs.
 */
export const usePendingRecheck = ({
  inquiryId,
  pendingCount,
  refresh,
  now = Date.now,
}: {
  inquiryId: string | undefined;
  pendingCount: number;
  /** Reads the Q&A again (its result is applied by the caller). */
  refresh: (inquiryId: string) => void;
  now?: () => number;
}) => {
  const latest = useRef(refresh);
  latest.current = refresh;

  useEffect(() => {
    if (!inquiryId || pendingCount === 0) return;
    const id = inquiryId;
    let delay = PENDING_RECHECK_MS;
    let timer: ReturnType<typeof setTimeout>;
    let lastRead = now();
    const read = () => {
      lastRead = now();
      latest.current(id);
    };
    const next = () => {
      timer = setTimeout(() => {
        if (!document.hidden) read();
        delay = Math.min(delay * 2, PENDING_RECHECK_MAX_MS);
        next();
      }, delay);
    };
    const onVisible = () => {
      if (document.hidden) return;
      clearTimeout(timer);
      if (now() - lastRead >= PENDING_RECHECK_MS) read();
      delay = PENDING_RECHECK_MS;
      next();
    };
    next();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [inquiryId, pendingCount, now]);
};
